import { useState } from 'react'

import { ballPasses, heatedSize, ru } from '../../lib/physics'
import { Predict, Slider } from './common'

/**
 * Шар и кольцо (урок 11): латунный шар проходит сквозь кольцо, нагретый — застревает,
 * а если нагреть и кольцо, проходит снова. Расстояния между молекулами твёрдого тела
 * тоже меняются — при нагревании растут. Отверстие в кольце растёт вместе с кольцом.
 */

const ШАР = 25 // мм при 20 °C
const ОТВЕРСТИЕ = 25.02 // мм при 20 °C
const PX_PER_MM = 5
const УВЕЛИЧЕНИЕ = 60 // во сколько раз на рисунке преувеличено расширение

export default function RingLab() {
  const [tШар, setTШар] = useState(20)
  const [tКольцо, setTКольцо] = useState(20)

  const dШар = heatedSize(ШАР, tШар)
  const dОтв = heatedSize(ОТВЕРСТИЕ, tКольцо)
  const проходит = ballPasses(ШАР, ОТВЕРСТИЕ, tШар, tКольцо)
  const r = (d: number, d20: number) => ((d20 + (d - d20) * УВЕЛИЧЕНИЕ) / 2) * PX_PER_MM
  const rШар = r(dШар, ШАР)
  const rОтв = r(dОтв, ОТВЕРСТИЕ)
  const класс = (t: number) => (t > 150 ? 'fill-hot' : t > 60 ? 'fill-warm' : 'fill-metal')

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 260" role="img" aria-label="Шар над кольцом">
        {/* кольцо — вид сбоку, в разрезе */}
        <rect x={180 - rОтв - 22} y={150} width={22} height={14} className={`${класс(tКольцо)} ink`} strokeWidth={1.5} />
        <rect x={180 + rОтв} y={150} width={22} height={14} className={`${класс(tКольцо)} ink`} strokeWidth={1.5} />
        <line x1={180 + rОтв + 22} y1={157} x2={330} y2={157} className="ink" strokeWidth={3} />
        {/* шар: проходит — опускается в кольцо, нет — лежит сверху */}
        <circle cx={180} cy={проходит ? 157 : 150 - Math.sqrt(Math.max(rШар ** 2 - rОтв ** 2, 0))} r={rШар} className={`${класс(tШар)} ink`} strokeWidth={1.5} />
        <text x={180} y={252} textAnchor="middle" className="lab-label">
          {проходит ? 'шар проходит сквозь кольцо' : 'шар застрял'}
        </text>
      </svg>

      <div className="lab-readout">
        <span>шар: <b>{ru(dШар, 3)} мм</b></span>
        <span>отверстие: <b>{ru(dОтв, 3)} мм</b></span>
        <span className="muted">расширение на рисунке увеличено в {УВЕЛИЧЕНИЕ} раз</span>
      </div>

      <Slider label="Температура шара" value={tШар} min={20} max={400} step={10} unit="°C" onChange={setTШар} />
      <Slider label="Температура кольца" value={tКольцо} min={20} max={400} step={10} unit="°C" onChange={setTКольцо} />

      <Predict
        question="Нагретый шар застрял. Что будет, если нагреть ещё и кольцо?"
        options={['отверстие сузится — застрянет крепче', 'отверстие расширится — шар пройдёт']}
        right={1}
        revealed={tШар >= 150 && tКольцо >= tШар}
        explain="Кольцо расширяется целиком, как будто увеличили фотографию, — отверстие растёт вместе с ним. Значит, и у твёрдого тела расстояния между молекулами при нагревании увеличиваются."
      />
      <p className="muted small">
        Нагрей шар до 300 °C — он больше всего на 0,13 мм, но этого хватает, чтобы застрять. Охлади его до 70 °C — ещё
        не пролезает: так в уроке шар, опущенный в горячую воду, остыл недостаточно.
      </p>
    </div>
  )
}
